import React from "react";
import { cn } from "@ndd/ui";
import type { RdHoursWeekdayName } from "../../../../../shared/api-types";
import {
  dayDateLabel,
  visitIsOutside,
  type WeekVisit,
  type WeekVisits,
} from "../../../lib/rd-hours-client";
import type { BlockRef, RdHoursMode } from "../../../lib/rd-hours-draft";
import {
  GRID_END_MINUTE,
  GRID_START_MINUTE,
  HOUR_HEIGHT_PX,
  RD_HOURS_DAYS,
  STEP_MINUTES,
  blockKeyAction,
  tooLongStretches,
  clockTime,
  dayName,
  dayShortName,
  hoursFigure,
  lowerBound,
  moveCursor,
  rangeLabel,
  timeLabel,
  upperBound,
  type GridCursor,
  type HoursBlock,
  type HoursDay,
  type HoursWeek,
} from "../../../lib/rd-hours-editor";
import { FOCUS, RULE } from "./look";

export interface RdHoursGridProps {
  /** The section's accessible name: "Weekly hours" or "Hours for Nov 2 to 8". */
  label: string;
  hint: string;
  mode: RdHoursMode;
  weekStart: string | null;
  days: HoursWeek;
  /** One week: the days this week replaces. */
  changed: readonly RdHoursWeekdayName[];
  selection: BlockRef | null;
  thresholdMinutes: number;
  /** One week with the switch on; null draws no visits. */
  visits: WeekVisits | null;
  /** Days edited since load: their visits are re-tested against the draft. */
  edited: readonly RdHoursWeekdayName[];
  onAddHour: (day: RdHoursWeekdayName, hourStart: number) => void;
  onCreate: (day: RdHoursWeekdayName, from: number, to: number) => void;
  onResize: (block: BlockRef, start: number, end: number) => void;
  onSelect: (block: BlockRef) => void;
  onDelete: (block: BlockRef) => void;
}

const HOURS = Array.from(
  { length: (GRID_END_MINUTE - GRID_START_MINUTE) / 60 },
  (_, index) => GRID_START_MINUTE / 60 + index
);
const GRID_HEIGHT_PX = HOURS.length * HOUR_HEIGHT_PX;
const DRAG_THRESHOLD_PX = 4;

/** A minute's distance from the grid's top edge. */
export function minuteTop(minute: number): number {
  return ((minute - GRID_START_MINUTE) / 60) * HOUR_HEIGHT_PX;
}

/** The minute under a pointer `offsetY` pixels below the grid's top edge. */
export function minuteAtOffset(offsetY: number): number {
  const minute = GRID_START_MINUTE + (offsetY / HOUR_HEIGHT_PX) * 60;

  return Math.min(GRID_END_MINUTE, Math.max(GRID_START_MINUTE, minute));
}

function snapToStep(minute: number): number {
  return Math.round(minute / STEP_MINUTES) * STEP_MINUTES;
}

/** The range a dragged edge gives a block, held inside its neighbours. */
export function draggedRange(
  day: HoursDay,
  index: number,
  edge: "top" | "bottom",
  minute: number
): { start: number; end: number } {
  const block = day[index]!;
  const snapped = snapToStep(minute);

  if (edge === "top") {
    const start = Math.min(
      block.end - STEP_MINUTES,
      Math.max(lowerBound(day, index), snapped)
    );

    return { start, end: block.end };
  }

  const end = Math.max(
    block.start + STEP_MINUTES,
    Math.min(upperBound(day, index), snapped)
  );

  return { start: block.start, end };
}

export function blockAriaLabel(
  day: RdHoursWeekdayName,
  block: HoursBlock,
  breakNote: string | null,
  selected: boolean
): string {
  return `${dayName(day)} ${rangeLabel(block)}${breakNote === null ? "" : `, ${breakNote}`}${selected ? ", selected" : ""}`;
}

type Preview =
  | { kind: "create"; day: RdHoursWeekdayName; from: number; to: number }
  | { kind: "resize"; block: BlockRef; start: number; end: number };

type Listeners = {
  move: (event: PointerEvent) => void;
  up: (event: PointerEvent) => void;
};

/** Pointer drags: a window-level pair of listeners from pointerdown to pointerup. */
function useDrag() {
  const active = React.useRef<Listeners | null>(null);

  const stop = React.useCallback(() => {
    if (active.current === null) return;

    window.removeEventListener("pointermove", active.current.move);
    window.removeEventListener("pointerup", active.current.up);
    window.removeEventListener("pointercancel", active.current.up);
    active.current = null;
  }, []);

  const start = React.useCallback(
    (listeners: Listeners) => {
      stop();
      active.current = listeners;
      window.addEventListener("pointermove", listeners.move);
      window.addEventListener("pointerup", listeners.up);
      window.addEventListener("pointercancel", listeners.up);
    },
    [stop]
  );

  React.useEffect(() => stop, [stop]);

  return { start, stop };
}

function HourGutter() {
  return (
    <div className="relative">
      {HOURS.map((hour, index) => (
        <span
          key={hour}
          className="absolute right-2 whitespace-nowrap font-mono text-caption text-muted-foreground"
          style={{ top: index * HOUR_HEIGHT_PX + 4 }}
        >
          {timeLabel(hour * 60)}
        </span>
      ))}
    </div>
  );
}

function DayHeader({
  day,
  props,
}: {
  day: RdHoursWeekdayName;
  props: Pick<RdHoursGridProps, "mode" | "weekStart" | "changed" | "days">;
}) {
  const inWeek = props.mode === "week" && props.weekStart !== null;
  const changed = inWeek && props.changed.includes(day);
  const sub = changed ? "Changed this week" : props.days[day].length === 0 ? "Off" : "";

  return (
    <div
      className={cn("box-border flex min-h-[50px] flex-col border-l px-2.5 py-2", RULE)}
    >
      <span className="flex items-baseline gap-1.5">
        <span className="font-semibold">{dayShortName(day)}</span>
        {inWeek && props.weekStart !== null ? (
          <span className="text-body-lg text-muted-foreground">
            {dayDateLabel(props.weekStart, day)}
          </span>
        ) : null}
      </span>
      <span className="text-label leading-4 text-muted-foreground">{sub}</span>
    </div>
  );
}

function VisitChip({ visit, outside }: { visit: WeekVisit; outside: boolean }) {
  const top = Math.max(0, minuteTop(visit.start)) + 2;
  const bottom = Math.min(
    GRID_HEIGHT_PX,
    minuteTop(Math.max(visit.end, visit.start + 15))
  );

  return (
    <span
      data-outside={outside ? "true" : "false"}
      className={cn(
        "pointer-events-none absolute left-[44%] right-1.5 z-[4] box-border overflow-hidden text-ellipsis whitespace-nowrap rounded-sm px-1.5 py-0.5 text-caption leading-[14px]",
        outside
          ? "border border-dashed border-danger bg-danger-soft text-danger"
          : "border border-border bg-background text-muted-strong"
      )}
      style={{ top, height: Math.max(14, bottom - top - 2) }}
    >
      <span className="font-mono">{clockTime(visit.start, false)}</span> {visit.name}
    </span>
  );
}

interface BlockViewProps {
  day: RdHoursWeekdayName;
  index: number;
  block: HoursBlock;
  range: { start: number; end: number };
  selected: boolean;
  /** The whole block needs a break: it has none, so its edge is red. */
  needsBreak: boolean;
  /** Spoken note for a block whose break leaves a stretch too long. */
  breakNote: string | null;
  /** The visually hidden line that lists the block's keys. */
  keysId: string;
  onSelect: (block: BlockRef) => void;
  onKeyDown: (event: React.KeyboardEvent, block: BlockRef, current: HoursBlock) => void;
  onEdgeDown: (
    event: React.PointerEvent,
    block: BlockRef,
    edge: "top" | "bottom"
  ) => void;
}

/** The keys a focused block answers (blockKeyAction), for aria-keyshortcuts. */
const BLOCK_SHORTCUTS =
  "Shift+ArrowUp Shift+ArrowDown Alt+ArrowUp Alt+ArrowDown Delete Backspace Enter";

/** A small grip bar on a resize edge: shown on hover, focus or selection. */
function Grip({ edge, selected }: { edge: "top" | "bottom"; selected: boolean }) {
  return (
    <span
      className={cn(
        "absolute left-1/2 h-[3px] w-5 -translate-x-1/2 rounded-full bg-border-strong transition-opacity motion-reduce:transition-none",
        edge === "top" ? "top-[3px]" : "bottom-[3px]",
        selected
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
      )}
    />
  );
}

function BlockView(props: BlockViewProps) {
  const { day, index, block, range, selected, needsBreak, breakNote } = props;
  const ref: BlockRef = { day, index };
  const length = range.end - range.start;

  return (
    <button
      type="button"
      aria-label={blockAriaLabel(day, { ...block, ...range }, breakNote, selected)}
      aria-pressed={selected}
      aria-describedby={props.keysId}
      aria-keyshortcuts={BLOCK_SHORTCUTS}
      data-block={`${day}-${index}`}
      onClick={() => props.onSelect(ref)}
      onKeyDown={(event) => props.onKeyDown(event, ref, block)}
      className={cn(
        "group absolute inset-x-1 z-[2] box-border flex flex-col items-start rounded-[10px] px-2 py-[5px] text-left text-body-sm font-medium leading-4 text-foreground",
        FOCUS,
        selected && "border-0 bg-primary-soft shadow-[inset_0_0_0_2px_var(--primary)]",
        !selected && needsBreak && "border-[1.5px] border-dashed border-danger bg-card",
        !selected && !needsBreak && "border border-border-strong bg-card"
      )}
      style={{
        top: minuteTop(range.start) + 1,
        height: minuteTop(range.end) - minuteTop(range.start) - 2,
      }}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 z-[5] h-2 cursor-ns-resize"
        onPointerDown={(event) => props.onEdgeDown(event, ref, "top")}
      >
        <Grip edge="top" selected={selected} />
      </span>
      <span className="w-full truncate font-semibold">{rangeLabel(range)}</span>
      {length >= 60 ? (
        <span className="text-label text-muted-foreground">
          {hoursFigure(length)} h
        </span>
      ) : null}
      {needsBreak ? (
        <span className="mt-0.5 font-semibold text-danger">Needs a break</span>
      ) : null}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 z-[5] h-2 cursor-ns-resize"
        onPointerDown={(event) => props.onEdgeDown(event, ref, "bottom")}
      >
        <Grip edge="bottom" selected={selected} />
      </span>
    </button>
  );
}

/** The one stretch a lunch break leaves too long: only it gets the red dashed edge. */
function LongStretchView({ stretch }: { stretch: { start: number; end: number } }) {
  return (
    <span
      aria-hidden="true"
      data-too-long-stretch=""
      className="pointer-events-none absolute inset-x-1 z-[2] box-border flex flex-col items-start justify-end rounded-[10px] border-[1.5px] border-dashed border-danger px-2 py-[5px] text-body-sm leading-4"
      style={{
        top: minuteTop(stretch.start) + 1,
        height: minuteTop(stretch.end) - minuteTop(stretch.start) - 2,
      }}
    >
      <span className="font-semibold text-danger">Too long without a break</span>
    </span>
  );
}

function LunchView({
  day,
  index,
  block,
  onSelect,
}: {
  day: RdHoursWeekdayName;
  index: number;
  block: HoursBlock;
  onSelect: (block: BlockRef) => void;
}) {
  const lunch = block.lunchBreak;

  if (lunch === null) return null;

  const end = lunch.start + lunch.minutes;

  return (
    <button
      type="button"
      aria-label={`Lunch break ${rangeLabel({ start: lunch.start, end })} on ${dayName(day)}`}
      onClick={() => onSelect({ day, index })}
      className={cn(
        "absolute inset-x-2.5 z-[3] box-border flex items-center rounded-sm border border-border-strong/40 px-2 text-meta font-semibold leading-[14px] text-foreground",
        // The canvas's darker second stop: no token sits at #e8e2c8.
        "bg-[repeating-linear-gradient(135deg,var(--muted)_0_5px,#e8e2c8_5px_6px)]",
        FOCUS
      )}
      style={{
        top: minuteTop(lunch.start) + 2,
        height: minuteTop(end) - minuteTop(lunch.start) - 4,
      }}
    >
      Lunch
    </button>
  );
}

function useGridKeyboard(gridRef: React.RefObject<HTMLDivElement | null>) {
  const [cursor, setCursor] = React.useState<GridCursor>({ day: 0, hour: 9 });

  function focusCell(next: GridCursor) {
    gridRef.current
      ?.querySelector<HTMLElement>(`[data-cell="${next.day}-${next.hour}"]`)
      ?.focus();
  }

  function onCellKeyDown(event: React.KeyboardEvent) {
    const next = moveCursor(cursor, event.key);

    if (next === null) return;

    event.preventDefault();
    setCursor(next);
    focusCell(next);
  }

  return { cursor, setCursor, onCellKeyDown };
}

function useEdgeDrag(
  props: RdHoursGridProps,
  drag: ReturnType<typeof useDrag>,
  setPreview: (preview: Preview | null) => void
) {
  return function onEdgeDown(
    event: React.PointerEvent,
    block: BlockRef,
    edge: "top" | "bottom"
  ) {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const dayBlocks = props.days[block.day];
    const column = (event.currentTarget as HTMLElement).closest("[data-column]");
    const offsetOf = (clientY: number) =>
      clientY - (column?.getBoundingClientRect().top ?? 0);
    const rangeAt = (clientY: number) =>
      draggedRange(dayBlocks, block.index, edge, minuteAtOffset(offsetOf(clientY)));

    drag.start({
      move: (move) => {
        setPreview({ kind: "resize", block, ...rangeAt(move.clientY) });
      },
      up: (up) => {
        drag.stop();
        setPreview(null);

        if (up.type === "pointercancel") return;

        const range = rangeAt(up.clientY);
        const current = dayBlocks[block.index]!;

        if (range.start !== current.start || range.end !== current.end) {
          props.onResize(block, range.start, range.end);
        }
      },
    });
  };
}

function useGridPointer(props: RdHoursGridProps) {
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const suppressClick = React.useRef(false);
  const drag = useDrag();

  function onColumnPointerDown(
    event: React.PointerEvent<HTMLDivElement>,
    day: RdHoursWeekdayName
  ) {
    suppressClick.current = false;

    const onCell = (event.target as HTMLElement).closest("[data-cell]") !== null;

    if (event.button !== 0 || event.pointerType === "touch" || !onCell) return;

    const column = event.currentTarget;
    const offsetOf = (clientY: number) => clientY - column.getBoundingClientRect().top;
    const anchor = minuteAtOffset(offsetOf(event.clientY));
    const startY = event.clientY;
    let dragging = false;

    drag.start({
      move: (move) => {
        if (!dragging && Math.abs(move.clientY - startY) < DRAG_THRESHOLD_PX) return;

        dragging = true;
        setPreview({
          kind: "create",
          day,
          from: anchor,
          to: minuteAtOffset(offsetOf(move.clientY)),
        });
      },
      up: (up) => {
        drag.stop();
        setPreview(null);

        if (!dragging || up.type === "pointercancel") return;

        suppressClick.current = true;
        props.onCreate(day, anchor, minuteAtOffset(offsetOf(up.clientY)));
      },
    });
  }

  function onCellClick(event: React.MouseEvent, day: RdHoursWeekdayName, hour: number) {
    // A drag ends in a click on the cell under the pointer; a key press has detail 0.
    if (suppressClick.current && event.detail !== 0) {
      suppressClick.current = false;

      return;
    }

    props.onAddHour(day, hour * 60);
  }

  const onEdgeDown = useEdgeDrag(props, drag, setPreview);

  return { preview, onColumnPointerDown, onEdgeDown, onCellClick };
}

function handleBlockKey(
  props: RdHoursGridProps,
  event: React.KeyboardEvent,
  block: BlockRef,
  current: HoursBlock
) {
  const action = blockKeyAction(event);

  if (action === null) return;

  event.preventDefault();

  switch (action.kind) {
    case "start":
      props.onResize(block, current.start + action.delta, current.end);
      break;
    case "end":
      props.onResize(block, current.start, current.end + action.delta);
      break;
    case "delete":
      props.onDelete(block);
      break;
    default:
      props.onSelect(block);
  }
}

function DayHeaderRow({ props }: { props: RdHoursGridProps }) {
  return (
    <div className={cn("grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b", RULE)}>
      <span />
      {RD_HOURS_DAYS.map((day) => (
        <DayHeader key={day} day={day} props={props} />
      ))}
    </div>
  );
}

/** The empty-cell grid's tab stop: where focus returns after a save. */
export function focusGrid(root: HTMLElement | null): void {
  root?.querySelector<HTMLElement>('[data-cell][tabindex="0"]')?.focus();
}

/** After a delete, the day's cell at the hour the block began. */
export function focusAfterDelete(
  root: HTMLElement | null,
  block: BlockRef,
  start: number
): void {
  const dayIndex = RD_HOURS_DAYS.indexOf(block.day);
  const hour = Math.min(
    GRID_END_MINUTE / 60 - 1,
    Math.max(GRID_START_MINUTE / 60, Math.floor(start / 60))
  );

  root?.querySelector<HTMLElement>(`[data-cell="${dayIndex}-${hour}"]`)?.focus();
}

/** The block a whole hour sits inside, if any: its cell adds nothing. */
export function coveringBlock(day: HoursDay, hour: number): HoursBlock | undefined {
  const start = hour * 60;

  return day.find((block) => block.start <= start && start + 60 <= block.end);
}

export function cellAriaLabel(
  day: RdHoursWeekdayName,
  hour: number,
  covering: HoursBlock | undefined
): string {
  const range = `${timeLabel(hour * 60)} to ${timeLabel((hour + 1) * 60)} on ${dayName(day)}`;

  return covering === undefined
    ? `Add ${range}`
    : `${range}, inside ${rangeLabel(covering)}`;
}

/** The hidden lines that tell a screen reader the keys for an empty cell and for a block. */
function GridKeyHelp({ keysId, cellKeysId }: { keysId: string; cellKeysId: string }) {
  return (
    <>
      <p id={cellKeysId} className="sr-only">
        Arrow keys move between hours and days. Enter or Space adds the hour.
      </p>
      <p id={keysId} className="sr-only">
        Shift with Up or Down Arrow moves the end, Alt with Up or Down Arrow moves the
        start, Delete removes these hours.
      </p>
    </>
  );
}

export function RdHoursGrid(props: RdHoursGridProps) {
  const gridRef = React.useRef<HTMLDivElement | null>(null);
  const { cursor, setCursor, onCellKeyDown } = useGridKeyboard(gridRef);
  const pointer = useGridPointer(props);
  const keysId = React.useId();
  const cellKeysId = React.useId();

  return (
    <section
      aria-label={props.label}
      className="min-w-0 grow-[999] basis-[640px] overflow-hidden rounded-xl border border-(color:--surface-line,var(--border)) bg-card"
    >
      <p
        className={cn(
          "m-0 border-b px-[18px] py-3 text-body-lg text-muted-strong",
          RULE
        )}
      >
        {props.hint}
      </p>
      <GridKeyHelp keysId={keysId} cellKeysId={cellKeysId} />
      <div className="overflow-x-auto">
        <div className="min-w-[660px]">
          <DayHeaderRow props={props} />
          <div
            ref={gridRef}
            className="grid select-none grid-cols-[56px_repeat(7,minmax(0,1fr))]"
            style={{ height: GRID_HEIGHT_PX }}
          >
            <HourGutter />
            {RD_HOURS_DAYS.map((day, dayIndex) => (
              <DayColumn
                key={day}
                day={day}
                dayIndex={dayIndex}
                grid={props}
                cursor={cursor}
                keysId={keysId}
                cellKeysId={cellKeysId}
                preview={pointer.preview}
                onCursor={setCursor}
                onCellKeyDown={onCellKeyDown}
                onCellClick={pointer.onCellClick}
                onPointerDown={pointer.onColumnPointerDown}
                onBlockKeyDown={(event, block, current) =>
                  handleBlockKey(props, event, block, current)
                }
                onEdgeDown={pointer.onEdgeDown}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface DayColumnProps {
  day: RdHoursWeekdayName;
  dayIndex: number;
  grid: RdHoursGridProps;
  cursor: GridCursor;
  /** The hidden lines that list a block's keys and an empty cell's keys. */
  keysId: string;
  cellKeysId: string;
  preview: Preview | null;
  onCursor: (cursor: GridCursor) => void;
  onCellKeyDown: (event: React.KeyboardEvent) => void;
  onCellClick: (event: React.MouseEvent, day: RdHoursWeekdayName, hour: number) => void;
  onPointerDown: (
    event: React.PointerEvent<HTMLDivElement>,
    day: RdHoursWeekdayName
  ) => void;
  onBlockKeyDown: BlockViewProps["onKeyDown"];
  onEdgeDown: BlockViewProps["onEdgeDown"];
}

function previewRange(
  preview: Preview | null,
  day: RdHoursWeekdayName,
  index: number,
  block: HoursBlock
): { start: number; end: number } {
  if (
    preview?.kind === "resize" &&
    preview.block.day === day &&
    preview.block.index === index
  ) {
    return { start: preview.start, end: preview.end };
  }

  return { start: block.start, end: block.end };
}

function HourCells({
  day,
  dayIndex,
  cursor,
  props,
}: {
  day: RdHoursWeekdayName;
  dayIndex: number;
  cursor: GridCursor;
  props: DayColumnProps;
}) {
  return (
    <div className="absolute inset-0">
      {HOURS.map((hour) => (
        <button
          key={hour}
          type="button"
          data-cell={`${dayIndex}-${hour}`}
          aria-label={cellAriaLabel(
            day,
            hour,
            coveringBlock(props.grid.days[day], hour)
          )}
          aria-describedby={props.cellKeysId}
          tabIndex={cursor.day === dayIndex && cursor.hour === hour ? 0 : -1}
          onFocus={() => props.onCursor({ day: dayIndex, hour })}
          onKeyDown={props.onCellKeyDown}
          onClick={(event) => props.onCellClick(event, day, hour)}
          className={cn(
            "absolute inset-x-0 z-[1] cursor-cell border-0 bg-transparent p-0 hover:bg-secondary/70",
            FOCUS,
            hour === GRID_START_MINUTE / 60
              ? "border-t border-t-transparent"
              : "border-t border-t-border"
          )}
          style={{
            top: (hour - GRID_START_MINUTE / 60) * HOUR_HEIGHT_PX,
            height: HOUR_HEIGHT_PX,
          }}
        />
      ))}
    </div>
  );
}

interface BlockBreakState {
  /** The block has no break and runs too long: the whole block's edge is red. */
  wholeBlock: boolean;
  /** The stretches a lunch break leaves too long: only they get the red edge. */
  stretches: { start: number; end: number }[];
  /** Spoken note for the block's label. */
  note: string | null;
}

function blockBreakState(block: HoursBlock, thresholdMinutes: number): BlockBreakState {
  const long = tooLongStretches([block], thresholdMinutes);

  if (long.length === 0) return { wholeBlock: false, stretches: [], note: null };

  if (block.lunchBreak === null) {
    return { wholeBlock: true, stretches: [], note: "needs a break" };
  }

  return {
    wholeBlock: false,
    stretches: long,
    note: `${long.map(rangeLabel).join(" and ")} is too long without a break`,
  };
}

/** The block is being dragged: its saved stretches would lag the drag preview. */
export function isResizing(
  preview: Preview | null,
  day: RdHoursWeekdayName,
  index: number
): boolean {
  return (
    preview?.kind === "resize" &&
    preview.block.day === day &&
    preview.block.index === index
  );
}

/** The too-long stretches a lunch break leaves; hidden for a block being dragged. */
export function LongStretches(props: {
  day: RdHoursWeekdayName;
  blocks: HoursDay;
  preview: Preview | null;
  thresholdMinutes: number;
}) {
  const { day, blocks, preview, thresholdMinutes } = props;

  return (
    <>
      {blocks.flatMap((block, index) =>
        isResizing(preview, day, index)
          ? []
          : blockBreakState(block, thresholdMinutes).stretches.map((stretch) => (
              <LongStretchView
                key={`long-${day}-${index}-${stretch.start}`}
                stretch={stretch}
              />
            ))
      )}
    </>
  );
}

function DayBlocks({ props }: { props: DayColumnProps }) {
  const { day, grid, preview } = props;
  const blocks = grid.days[day];

  return (
    <>
      {blocks.map((block, index) => {
        const range = previewRange(preview, day, index, block);
        const state = blockBreakState(block, grid.thresholdMinutes);

        return (
          <BlockView
            key={`${day}-${index}`}
            day={day}
            index={index}
            block={block}
            range={range}
            selected={grid.selection?.day === day && grid.selection.index === index}
            needsBreak={state.wholeBlock}
            breakNote={state.note}
            keysId={props.keysId}
            onSelect={grid.onSelect}
            onKeyDown={props.onBlockKeyDown}
            onEdgeDown={props.onEdgeDown}
          />
        );
      })}
      <LongStretches
        day={day}
        blocks={blocks}
        preview={preview}
        thresholdMinutes={grid.thresholdMinutes}
      />
      {blocks.map((block, index) => (
        <LunchView
          key={`lunch-${day}-${index}`}
          day={day}
          index={index}
          block={block}
          onSelect={grid.onSelect}
        />
      ))}
    </>
  );
}

function DayVisits({ day, grid }: { day: RdHoursWeekdayName; grid: RdHoursGridProps }) {
  const edited = grid.edited.includes(day);

  if (grid.visits === null) return null;

  return (
    <>
      {grid.visits[day]
        .filter(
          (visit) => visit.end > GRID_START_MINUTE && visit.start < GRID_END_MINUTE
        )
        .map((visit) => (
          <VisitChip
            key={visit.id}
            visit={visit}
            outside={visitIsOutside(visit, grid.days, day, edited)}
          />
        ))}
    </>
  );
}

function DayColumn(props: DayColumnProps) {
  const { day, dayIndex, grid, cursor, preview } = props;

  return (
    // A labelled group per day: its hours, blocks and lunch breaks together.
    <div
      data-column={day}
      role="group"
      aria-label={dayName(day)}
      className={cn("relative border-l bg-muted", RULE)}
      onPointerDown={(event) => props.onPointerDown(event, day)}
    >
      <HourCells day={day} dayIndex={dayIndex} cursor={cursor} props={props} />
      <DayBlocks props={props} />
      {preview?.kind === "create" && preview.day === day ? (
        <CreatePreview from={preview.from} to={preview.to} />
      ) : null}
      <DayVisits day={day} grid={grid} />
    </div>
  );
}

function CreatePreview({ from, to }: { from: number; to: number }) {
  const low = Math.min(snapToStep(Math.min(from, to)), GRID_END_MINUTE - STEP_MINUTES);
  const high = Math.max(snapToStep(Math.max(from, to)), low + STEP_MINUTES);

  return (
    <span
      aria-hidden="true"
      data-preview="create"
      className="pointer-events-none absolute inset-x-1 z-[2] box-border rounded-[10px] border border-dashed border-primary bg-primary-soft/60"
      style={{
        top: minuteTop(low) + 1,
        height: minuteTop(Math.min(high, GRID_END_MINUTE)) - minuteTop(low) - 2,
      }}
    />
  );
}
